import {Button} from '@mui/material';
import React, {useState, useRef, useEffect} from 'react';
import moment from 'moment';

import LeftArrowIcon from '$assets/svgs/chevron-left-arrow.svg?react';
import SearchRefractionIcon from '$assets/svgs/search-refraction.svg?react';
import RigthArrowIcon from '$assets/svgs/chevron-right-arrow.svg?react';
import FrontdeskSearchbox from '$components/FrontdeskSearchBox';
import FrontdeskButton from '$components/FrontdeskButton';
import PaymentListTable from './PaymentListTable';
import SearchPatientContainer from '../../common/components/SearchPatientModel';
import {
  PAYMENT_LIST_PAGE_SIZE,
  PAYMENT_LIST_STRINGS,
  PaymentListOrderBy,
  PaymentStatus,
} from './constant';
import '../styles.scss';
import {PaymentInfo} from './types';
import '../../Calendar/CalendarView/styles.scss';
import CalendarFilter from './CalendarFilter';
import NewPaymentIcon from '$assets/svgs/new-payment.svg?react';
import FilterFunnelIcon from '$assets/svgs/filter-funnel.svg?react';
import {PaymentListFilterDropdown} from './PaymentListFilterDropdown';

interface PaymentListComponentProps {
  isLoading: boolean;
  isError: boolean;
  message: string;
  searchText: string;
  currentPage: number;
  payments: PaymentInfo[];
  totalPages: number;
  totalRecords: number;
  sortActiveColumn?: PaymentListOrderBy;
  sortDirection?: boolean;
  status?: PaymentStatus;
  setSortActiveColumn: (value?: PaymentListOrderBy) => void;
  setSortDirection: React.Dispatch<React.SetStateAction<boolean>>;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onSearchTextChange: (value: string) => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  onSort?: (column: PaymentListOrderBy) => void;
  onStatusChange: (status: PaymentStatus | undefined) => void;
  paidCount: number;
  unpaidCount: number;
  allPaymentCount: number;
  currentDate: Date | null;
  setCurrentDate: (date: Date | null) => void;
  viewMode: 'day' | 'week' | 'month';
  setViewMode: (mode: 'day' | 'week' | 'month') => void;
  // Filter props
  filterModalOpen: boolean;
  setFilterModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedPaymentMethods: string[];
  setSelectedPaymentMethods: React.Dispatch<React.SetStateAction<string[]>>;
  amountRange: number[];
  setAmountRange: React.Dispatch<React.SetStateAction<number[]>>;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  minAmount: number;
  maxAmount: number;
}

interface StatusFilterButtonProps {
  label: string;
  count: number;
  isActive: boolean;
  onStatusFilterClick: () => void;
  isLoading?: boolean;
}

const StatusFilterButton = ({
  label,
  count,
  isActive,
  onStatusFilterClick,
  isLoading,
}: StatusFilterButtonProps) => (
  <Button
    variant="outlined"
    className={`h-8 px-2 py-1 text-sm font-normal payment-status-button ${
      isActive ? 'active' : ''
    }`}
    onClick={onStatusFilterClick}
    disabled={isLoading}
  >
    {label} ({count})
  </Button>
);

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
  isLoading?: boolean;
}

const PaginationControls = ({
  currentPage,
  totalPages,
  onPreviousPage,
  onNextPage,
}: PaginationControlsProps) => (
  <div className="pagination-container flex">
    <Button
      className="p-1 px-2 flex items-center page-change-button"
      onClick={onPreviousPage}
    >
      <LeftArrowIcon />
    </Button>
    <div className="p-1 px-6 flex items-center border-left border-solid border-x border-y-0 border-color">
      <span>
        {currentPage.toString().padStart(2, '0')} /{' '}
        {totalPages.toString().padStart(2, '0')}
      </span>
    </div>
    <Button
      className="p-1 px-2 flex items-center page-change-button"
      onClick={onNextPage}
    >
      <RigthArrowIcon />
    </Button>
  </div>
);

function PaymentListComponent(props: PaymentListComponentProps) {
  const [newAppointmentModalOpen, setNewAppointmentModalOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const {
    isLoading,
    isError,
    message,
    searchText,
    currentPage,
    payments,
    totalPages,
    totalRecords,
    sortActiveColumn,
    sortDirection,
    status,
    setSortActiveColumn,
    setSortDirection,
    onNextPage,
    onPreviousPage,
    onSearchTextChange,
    setCurrentPage,
    onSort,
    onStatusChange,
    paidCount,
    unpaidCount,
    allPaymentCount,
    currentDate,
    setCurrentDate,
    viewMode,
    setViewMode,
    selectedPaymentMethods,
    setSelectedPaymentMethods,
    amountRange,
    setAmountRange,
    onApplyFilters,
    onResetFilters,
    minAmount,
    maxAmount,
  } = props;

  const paymentMethodOptions = [
    'Stripe',
    'Cash',
    'ApplePay',
    'PayPal',
    'Cheque',
  ];

  const handlePaymentMethodChange = (method: string) => {
    setSelectedPaymentMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  };

  const handleAmountRangeChange = (_: Event, newValue: number | number[]) => {
    setAmountRange(newValue as number[]);
  };

  const handleResetFilters = () => {
    onResetFilters();
  };

  const handleApplyFilters = () => {
    onApplyFilters();
    setFilterDropdownOpen(false);
  };

  const handleFilterButtonClick = () => {
    setIsFilterButtonClicked(true);
    const newState = !filterDropdownOpen;
    setFilterDropdownOpen(newState);
  };

  const [isFilterButtonClicked, setIsFilterButtonClicked] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node) &&
        !isFilterButtonClicked
      ) {
        setFilterDropdownOpen(false);
      }

      if (isFilterButtonClicked) {
        setTimeout(() => setIsFilterButtonClicked(false), 100);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterButtonClicked]);

  const getRangeLabel = () => {
    const m = moment.isMoment(currentDate) ? currentDate : moment(currentDate);
    if (viewMode === 'week') {
      const start = m.clone().startOf('week');
      const end = m.clone().endOf('week');
      return `${start.format('DD')} - ${end.format('DD MMM')}`;
    } else if (viewMode === 'day') {
      return m.format('DD MMM');
    } else if (viewMode === 'month') {
      return m.format('MMMM YYYY');
    }
    return '';
  };

  const changeDateRange = (direction: 'next' | 'prev') => {
    if (!currentDate) return;
    const m = moment(currentDate);
    const amount = direction === 'next' ? 1 : -1;
    if (viewMode === 'week') setCurrentDate(m.add(amount, 'week').toDate());
    else if (viewMode === 'day') setCurrentDate(m.add(amount, 'day').toDate());
    else if (viewMode === 'month')
      setCurrentDate(m.add(amount, 'month').toDate());
  };

  const handleOnChange = (searchQuery = '') => {
    setCurrentPage(1);
    onSearchTextChange(searchQuery);
  };

  const currentRangeStart =
    (currentPage - 1) * PAYMENT_LIST_PAGE_SIZE + (payments.length ? 1 : 0);
  const currentRangeEnd =
    currentRangeStart + payments.length - (payments.length ? 1 : 0);

  const summary = `Displaying ${currentRangeStart} - ${currentRangeEnd} of ${totalRecords} Payments`;

  return (
    <div className="payment-list-container flex flex-col flex-1 p-10">
      <div className="flex justify-between mb-9">
        <h1 className="text-2xl font-semibold primary-dark">Payment list</h1>
        <div className="flex gap-4 items-center">
          <FrontdeskButton
            title="New payment"
            startIcon={<NewPaymentIcon />}
            onClick={() => setNewAppointmentModalOpen(true)}
          />
          <FrontdeskSearchbox
            value={searchText}
            startIcon={<SearchRefractionIcon />}
            onChange={handleOnChange}
            placeholder="Search"
          />
        </div>
      </div>

      <div className="flex gap-4 mb-6 items-center justify-between">
        <div className="flex gap-4">
          <StatusFilterButton
            label={PAYMENT_LIST_STRINGS.ALL_PAYMENTS}
            count={allPaymentCount}
            isActive={!status}
            onStatusFilterClick={() => onStatusChange(undefined)}
            isLoading={isLoading}
          />
          <StatusFilterButton
            label={PAYMENT_LIST_STRINGS.PAID}
            count={paidCount}
            isActive={status === PaymentStatus.Paid}
            onStatusFilterClick={() => onStatusChange(PaymentStatus.Paid)}
            isLoading={isLoading}
          />
          <StatusFilterButton
            label={PAYMENT_LIST_STRINGS.UNPAID}
            count={unpaidCount}
            isActive={status === PaymentStatus.Unpaid}
            onStatusFilterClick={() => onStatusChange(PaymentStatus.Unpaid)}
            isLoading={isLoading}
          />
        </div>
        <div className="flex gap-4 mb-4 items-center justify-between">
          <CalendarFilter
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            viewMode={viewMode}
            setViewMode={setViewMode}
            getRangeLabel={getRangeLabel}
            changeDateRange={changeDateRange}
          />
          <div ref={filterRef} className="relative">
            <FrontdeskButton
              variant="secondary"
              title="Filter"
              startIcon={<FilterFunnelIcon />}
              onClick={handleFilterButtonClick}
              className="filter-button-dark"
            />
            <PaymentListFilterDropdown
              open={filterDropdownOpen}
              anchorRef={filterRef}
              paymentMethodOptions={paymentMethodOptions}
              selectedPaymentMethods={selectedPaymentMethods}
              handlePaymentMethodChange={handlePaymentMethodChange}
              amountRange={amountRange}
              handleAmountRangeChange={handleAmountRangeChange}
              handleResetFilters={handleResetFilters}
              handleApplyFilters={handleApplyFilters}
              minAmount={minAmount}
              maxAmount={maxAmount}
              onClose={() => setFilterDropdownOpen(false)}
            />
          </div>
        </div>
      </div>

      {isError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
          <span className="text-red-600">{message}</span>
        </div>
      )}

      <PaymentListTable
        isLoading={isLoading}
        isError={isError}
        message={message}
        payments={payments}
        sortActiveColumn={sortActiveColumn}
        sortDirection={sortDirection}
        setSortActiveColumn={setSortActiveColumn}
        setSortDirection={setSortDirection}
        onSort={onSort}
      />
      <div className="flex justify-between mt-10">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          isLoading={isLoading}
        />
        <div>
          <span className="text-sm tertiary">{summary}</span>
        </div>
      </div>

      {newAppointmentModalOpen && (
        <SearchPatientContainer
          newAppointmentModalOpen={newAppointmentModalOpen}
          handleNewAppointmentModalOpen={() =>
            setNewAppointmentModalOpen(false)
          }
        />
      )}
    </div>
  );
}

export default PaymentListComponent;
